import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function FeaturedCarousel({ vehicles, isLoading = false }) {
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(1);
  const previousSlotsRef = useRef(new Map());

  useEffect(() => {
    if (vehicles.length === 0) {
      setFeaturedVehicles([]);
      return;
    }

    const availableVehicles = vehicles.filter((vehicle) => !vehicle.isSold);
    const source = availableVehicles.length > 0 ? availableVehicles : vehicles;
    const shuffled = [...source].sort(() => Math.random() - 0.5);

    setFeaturedVehicles(shuffled.slice(0, 5));
    setActiveIndex(0);
    previousSlotsRef.current = new Map();
  }, [vehicles]);

  useEffect(() => {
    if (featuredVehicles.length <= 1 || isPaused) {
      return undefined;
    }

    const interval = setInterval(() => {
      setDirection(1);
      setActiveIndex((current) => (current + 1) % featuredVehicles.length);
    }, 3500);

    return () => clearInterval(interval);
  }, [featuredVehicles, isPaused]);

  const handlePrevious = () => {
    if (featuredVehicles.length <= 1) {
      return;
    }

    setDirection(-1);
    setActiveIndex(
      (current) => (current - 1 + featuredVehicles.length) % featuredVehicles.length
    );
  };

  const handleNext = () => {
    if (featuredVehicles.length <= 1) {
      return;
    }

    setDirection(1);
    setActiveIndex((current) => (current + 1) % featuredVehicles.length);
  };

  const cards = useMemo(() => {
    const count = featuredVehicles.length;

    if (count === 0) {
      return [];
    }

    const centerIndex = Math.floor(count / 2);
    const nextSlots = new Map();

    const builtCards = featuredVehicles.map((vehicle, index) => {
      const slot = (index - activeIndex + count) % count;
      const previousSlot = previousSlotsRef.current.get(vehicle.id);
      const wrapsFromLeftToRight = direction === 1 && previousSlot === 0 && slot === count - 1;
      const wrapsFromRightToLeft = direction === -1 && previousSlot === count - 1 && slot === 0;
      const shouldSkipTransition = wrapsFromLeftToRight || wrapsFromRightToLeft;
      const isCenterCard = slot === centerIndex;

      nextSlots.set(vehicle.id, slot);

      return {
        id: vehicle.id,
        slot,
        isCenterCard,
        shouldSkipTransition,
        vehicle,
      };
    });

    previousSlotsRef.current = nextSlots;
    return builtCards;
  }, [featuredVehicles, activeIndex, direction]);

  if (isLoading) {
    return (
      <section className="featured-carousel-fullwidth">
        <div className="featured-carousel-header">
          <h2>Featured Stock</h2>
        </div>

        <div className="featured-carousel-viewport" aria-label="Loading featured stock">
          <div className="featured-carousel-stage">
            <div className="featured-carousel-track featured-carousel-track-loading">
              {Array.from({ length: 3 }).map((_, index) => (
                <article className="featured-carousel-skeleton-card" key={`featured-skeleton-${index}`}>
                  <div className="featured-carousel-skeleton-image" />
                  <div className="featured-carousel-skeleton-body">
                    <span className="featured-carousel-skeleton-line featured-carousel-skeleton-line-title" />
                    <span className="featured-carousel-skeleton-line featured-carousel-skeleton-line-price" />
                    <span className="featured-carousel-skeleton-line featured-carousel-skeleton-line-finance" />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (cards.length === 0) {
    return null;
  }

  return (
    <section className="featured-carousel-fullwidth">
      <div className="featured-carousel-header">
        <h2>Featured Stock</h2>
      </div>

      <div
        className="featured-carousel-viewport"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="featured-carousel-stage">
          <button
            type="button"
            className="featured-carousel-control featured-carousel-control-left"
            onClick={handlePrevious}
            aria-label="Previous featured vehicles"
          >
            ‹
          </button>

          <div className="featured-carousel-track" style={{ '--carousel-slots': cards.length }}>
            {cards.map((card) => (
              <Link
                className={`featured-carousel-card ${card.isCenterCard ? 'featured-carousel-card-large' : ''} ${card.shouldSkipTransition ? 'featured-carousel-card-no-transition' : ''}`}
                key={card.id}
                style={{ '--carousel-slot': card.slot }}
                to={`/vehicle/${card.id}`}
                aria-label={`View details for ${card.vehicle.make} ${card.vehicle.model}`}
              >
                <div className="featured-carousel-image-wrap">
                  <img
                    src={card.vehicle.imageUrls?.[0] || card.vehicle.imageUrl}
                    alt={`${card.vehicle.make} ${card.vehicle.model}`}
                  />
                </div>
                <div className="featured-carousel-body">
                  <h3>
                    {card.vehicle.make} {card.vehicle.model}
                  </h3>
                  <strong>£{Number(card.vehicle.salePrice).toLocaleString()}</strong>
                  <span>
                    Finance from £{Number(card.vehicle.financePrice).toLocaleString()}/month
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <button
            type="button"
            className="featured-carousel-control featured-carousel-control-right"
            onClick={handleNext}
            aria-label="Next featured vehicles"
          >
            ›
          </button>
        </div>

        <div className="featured-carousel-finance-note" aria-label="Representative finance example">
          <p>
            <strong>Representative Example for Hire Purchase (HP):</strong> Cash price £4,495.00, Annual Interest Rate (fixed) 6.81% p.a., with a representative 12.9% APR, total amount of credit £4,046.00, deposit of £449.00, an initial payment of £90.40, followed by 58 monthly payments of £90.40 with a final payment of £90.40, total amount payable is £5,873.00.
          </p>
        </div>
      </div>
    </section>
  );
}

export default FeaturedCarousel;